import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ShieldCheck, Award, GraduationCap, Stethoscope } from "lucide-react";

const faqData = [
  {
    question: "What is the difference between a dietitian and a nutritionist in South Africa?",
    answer: "In South Africa, a registered dietitian is a qualified healthcare professional regulated by the Health Professions Council of South Africa (HPCSA) and must hold a recognised degree. The title 'nutritionist' is not legally regulated, so anyone can call themselves one. For evidence-based, safe nutrition advice, always consult a registered dietitian."
  },
  {
    question: "Do you accept medical aid?",
    answer: "Yes. We invoice at medical aid rates and work with all major South African medical schemes. Depending on your plan, consultations may be covered from your available day-to-day or allied-health benefits."
  },
  {
    question: "What conditions do you treat?",
    answer: "We support a wide range of nutrition concerns including IBS, bloating, reflux, food intolerances, weight management, type 2 diabetes, high cholesterol, PCOS, pregnancy nutrition, paediatric feeding, sports nutrition and general healthy eating."
  },
  {
    question: "Do you offer online consultations?",
    answer: "Yes. We offer secure virtual consultations to clients anywhere in South Africa, as well as in-person appointments at our Glenhazel practice in Johannesburg."
  },
  {
    question: "What happens during the first consultation?",
    answer: "Your first session is a comprehensive assessment. We review your medical history, current eating habits, lifestyle, goals and preferences, then create a personalised, practical plan you can start using immediately."
  },
  {
    question: "Where is your practice located?",
    answer: "Our practice is at 48 Tanced Road, Glenhazel, Johannesburg, 2192. We also serve clients across Sandton, Randburg, Fourways, Rosebank and surrounding areas via in-person or online consultations."
  }
];

const Index = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqData.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer
      }
    }))
  };

  return (
    <div>
      <Helmet>
        <title>Dieticians Near Me In Johannesburg | Functional Foodie</title>
        <meta 
          name="description" 
          content="Find expert dieticians near you in Johannesburg. Functional Foodie offers personalised nutrition plans for a healthier lifestyle. Book your consultation today!"
        />
        <link rel="canonical" href="https://functionalfoodie.co.za/" />
        <meta property="og:title" content="Dieticians Near Me in Johannesburg | Functional Foodie" />
        <meta property="og:description" content="Find expert dieticians near you in Johannesburg. Functional Foodie offers personalised nutrition plans for a healthier lifestyle. Book your consultation today!" />
        <meta property="og:url" content="https://functionalfoodie.co.za/" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      {/* Hero Section */}
      <section className="relative h-screen bg-[url('https://images.unsplash.com/photo-1490818387583-1baba5e638af?q=80&w=1932&auto=format&fit=crop')] bg-cover bg-center">
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative container mx-auto px-4 h-full flex flex-col items-center justify-center text-center pt-20">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 text-white">
            Welcome to <span className="text-secondary">Functional Foodie</span> Registered Dietitians
          </h1>
          <p className="text-xl md:text-2xl mb-12 text-white max-w-3xl">
            Discover personalised nutritional solutions that transform your health and well-being with our expert registered dietitians.
          </p>
          <Link to="/contact">
            <Button 
              size="lg" 
              className="bg-secondary hover:bg-secondary-light text-secondary-foreground text-lg px-8 rounded-full"
            >
              CONTACT US
            </Button>
          </Link>
        </div>
      </section>

      {/* Booking CTA Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl text-primary max-w-4xl mx-auto mb-8">
            Take control of your health today! Book your appointment online to get personalised nutritional advice and start your journey to healthier you.
          </h2>
          <Link to="/booking">
            <Button size="lg" className="bg-primary hover:bg-primary-light text-white text-lg px-12 py-6 rounded-full">
              BOOK YOUR APPOINTMENT
            </Button>
          </Link>
        </div>
      </section>

      {/* Trust / Authority Section */}
      <section className="py-20 bg-accent/30">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-primary text-center mb-4">
            Trusted, Registered Dietitians
          </h2>
          <p className="text-center text-primary/80 max-w-2xl mx-auto mb-12">
            Your health is too important to leave to unqualified advice. Our practice is built on recognised credentials, evidence-based care and professional accountability.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            <div className="bg-white rounded-2xl p-6 text-center shadow-sm">
              <Award className="w-10 h-10 text-secondary mx-auto mb-4" aria-hidden="true" />
              <h3 className="font-bold text-primary mb-2">HPCSA Registered</h3>
              <p className="text-sm text-primary/80">
                Dalya Hockman is a registered dietitian with the Health Professions Council of South Africa (HPCSA), the official statutory body for healthcare professionals.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-6 text-center shadow-sm">
              <GraduationCap className="w-10 h-10 text-secondary mx-auto mb-4" aria-hidden="true" />
              <h3 className="font-bold text-primary mb-2">ADSA Member</h3>
              <p className="text-sm text-primary/80">
                Member of the Association for Dietetics in South Africa (ADSA), the professional organisation representing registered dietitians nationwide.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-6 text-center shadow-sm">
              <Stethoscope className="w-10 h-10 text-secondary mx-auto mb-4" aria-hidden="true" />
              <h3 className="font-bold text-primary mb-2">Evidence-Based Care</h3>
              <p className="text-sm text-primary/80">
                All advice is grounded in current scientific evidence and tailored to your medical history, lifestyle and personal goals.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-6 text-center shadow-sm">
              <ShieldCheck className="w-10 h-10 text-secondary mx-auto mb-4" aria-hidden="true" />
              <h3 className="font-bold text-primary mb-2">Medical Aid Rates</h3>
              <p className="text-sm text-primary/80">
                We invoice at recognised medical aid rates and work with all major South African medical schemes for your convenience.
              </p>
            </div>
          </div>
          <div className="text-center mt-10">
            <Link to="/team">
              <Button variant="outline" className="rounded-full px-8 text-primary border-primary hover:bg-primary hover:text-white">
                MEET THE TEAM
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-primary text-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16">
            Our <span className="text-secondary">Process</span>
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {/* Initial Consult */}
            <div className="space-y-4">
              <div className="rounded-3xl overflow-hidden">
                <img 
                  src="/lovable-uploads/6dbcc763-b40f-457c-922c-2231c52899cd.png" 
                  alt="Initial consultation with dietitian and fresh vegetables" 
                  className="w-full h-48 object-cover"
                />
              </div>
              <h3 className="text-2xl font-semibold">1. Initial Consult</h3>
              <p className="text-accent leading-relaxed">
                The initial consultation is where our journey begins. It is an assessment session where we consider your dietary history, preferences, relevant medical conditions, and overall eating habits. This information will help us create a tailor-made meal plan that should be both enjoyable and sustainable.
              </p>
            </div>

            {/* Meal Plan Collection */}
            <div className="space-y-4">
              <div className="rounded-3xl overflow-hidden">
                <img 
                  src="/lovable-uploads/13cb5415-22f5-4c82-bd6b-98636ecf3990.png" 
                  alt="Meal plan with fresh ingredients and clipboard" 
                  className="w-full h-48 object-cover"
                />
              </div>
              <h3 className="text-2xl font-semibold">2. Meal Plan Collection</h3>
              <p className="text-accent leading-relaxed">
                After your initial consultation, I will create a tailor-made meal plan for your individual needs. This session is where you will collect your meal plan and we will go through it together in detail and ensure that you are satisfied with the plan forward.
              </p>
            </div>

            {/* Follow Ups */}
            <div className="space-y-4">
              <div className="rounded-3xl overflow-hidden">
                <img 
                  src="/lovable-uploads/9d48fdc0-266b-45c0-b6ce-e157fa6e6eaa.png" 
                  alt="Healthy ingredients including avocado, grilled chicken, and fresh vegetables floating above a spoon" 
                  className="w-full h-48 object-cover"
                />
              </div>
              <h3 className="text-2xl font-semibold">3. Follow Ups</h3>
              <p className="text-accent leading-relaxed">
                Follow up consultations are extremely important to track your progress and make any changes that may be necessary. The continuous support and education is designed to assist you to keep on track.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold text-primary text-center mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-center text-primary/80 mb-10">
            Quick answers to the questions we hear most often. Can't find what you need? <Link to="/contact" className="text-secondary hover:underline">Contact us</Link>.
          </p>
          <Accordion type="single" collapsible className="w-full">
            {faqData.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-left text-primary font-semibold text-lg">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-primary/80 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
};

export default Index;
